import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Header } from './components/shared/Header';
import { ProductLanding } from './components/ProductLanding';
import { PDFWorkflow } from './components/pdf/PDFWorkflow';
import { FeatureCaptureWorkspace } from './components/featureCapture/FeatureCaptureWorkspace';
import { DashboardWorkspace } from './components/dashboard/DashboardWorkspace';
import { ApplicationPortal } from './components/applications/ApplicationPortal';
import { useApplicationCatalog } from './components/hooks/useApplicationCatalog';
import { AdminInvitesPage } from './components/admin/AdminInvitesPage';
import { RequestsCenterPage } from './components/requests/RequestsCenterPage';
import { LoginModal } from './components/auth/LoginModal';
import { MFAManagementModal } from './components/auth/MFAManagementModal';
import { API_ENDPOINTS } from './constants/api';
import { apiFetch } from './utils/apiClient';
import { resolveRequestPath } from './utils/requestNavigation';
import { JobProvider } from './components/jobs/JobProvider';
import { JobToastRegion } from './components/jobs/JobToastRegion';
import {
  consumeWorkspaceBackRoute,
  recordWorkspaceRoute,
  wasCurrentDocumentReloaded,
} from './utils/workspaceNavigationHistory';
import {
  applicationModeFromPath,
  buildApplicationPath,
  buildApplicationsPath,
  parseApplicationRoute,
} from './utils/applicationRoutes';

const routeForMode = (mode) => {
  if (mode === 'applications' || mode === 'application') return buildApplicationsPath();
  if (mode === 'pdf') return buildApplicationsPath('brd');
  if (mode === 'requirements') return buildApplicationsPath('requirements');
  if (mode === 'api-testing') return buildApplicationsPath('api');
  if (mode === 'excel-testcases') return buildApplicationsPath('excel');
  if (mode === 'feature-capture') return buildApplicationsPath('feature');
  if (mode === 'dashboard') return '/dashboard';
  if (mode === 'admin') return '/admin';
  if (mode === 'requests') return '/requests';
  return '/';
};

const modeFromPath = (pathname) => {
  if (pathname.startsWith('/applications')) return applicationModeFromPath(pathname);
  if (pathname.startsWith('/pdf')) return 'pdf';
  if (pathname.startsWith('/requirements')) return 'requirements';
  if (pathname.startsWith('/api-testing')) return 'api-testing';
  if (pathname.startsWith('/excel-testcases')) return 'excel-testcases';
  if (pathname.startsWith('/feature-capture')) return 'feature-capture';
  if (pathname.startsWith('/dashboard')) return 'dashboard';
  if (pathname.startsWith('/admin')) return 'admin';
  if (pathname.startsWith('/requests')) return 'requests';
  return 'landing';
};

const REQUESTS_RETURN_PATH_KEY = 'testsuite_requests_return_path';
const AUTH_RETURN_PATH_KEY = 'testsuite_auth_return_path';
const LAST_APPLICATION_HOME_PATH_KEY = 'testsuite_last_application_home_path';

const buildFullPath = (location) => (
  `${location.pathname || '/'}${location.search || ''}${location.hash || ''}`
);

const sanitizeAuthReturnPath = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized || normalized === '/' || normalized.startsWith('/login')) return '';
  return normalized.startsWith('/') ? normalized : '';
};

const sanitizeReturnPath = (value, fallback = buildApplicationsPath()) => {
  const normalized = String(value || '').trim();
  if (!normalized || normalized.startsWith('/requests')) return fallback;
  return normalized.startsWith('/') ? normalized : fallback;
};

const sanitizeApplicationHomePath = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized.startsWith('/applications/')) return '';
  const pathname = normalized.split(/[?#]/)[0];
  const route = parseApplicationRoute(pathname);
  return route.applicationId ? buildApplicationPath(route.applicationId) : '';
};

const AuthHydrationScreen = () => (
  <main className="auth-hydration-screen" aria-label="Loading TestSuite AI">
    <div className="auth-hydration-card" role="status">
      <img src="/metaz-logo.png" alt="" className="auth-hydration-logo" />
      <div>
        <p>TestSuite AI</p>
        <span>Loading workspace...</span>
      </div>
    </div>
  </main>
);

const WorkspaceShell = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const lastNonRequestsPathRef = useRef(buildApplicationsPath());
  const currentMode = modeFromPath(location.pathname);

  const rememberAuthReturnPath = useCallback((path = '') => {
    const fallbackPath = `${location.pathname || '/'}${location.search || ''}${location.hash || ''}`;
    const returnPath = sanitizeAuthReturnPath(path || fallbackPath);
    if (returnPath) {
      sessionStorage.setItem(AUTH_RETURN_PATH_KEY, returnPath);
    }
  }, [location.hash, location.pathname, location.search]);

  const consumeAuthReturnPath = useCallback(() => {
    const returnPath = sanitizeAuthReturnPath(sessionStorage.getItem(AUTH_RETURN_PATH_KEY));
    sessionStorage.removeItem(AUTH_RETURN_PATH_KEY);
    return returnPath;
  }, []);

  const [currentUser, setCurrentUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaModalOpen, setMfaModalOpen] = useState(false);
  const [notificationSummary, setNotificationSummary] = useState({
    brd_pending: 0,
    sharing_pending: 0,
    test_case_pending: 0,
    total_pending: 0,
  });
  const [notificationItems, setNotificationItems] = useState([]);
  const [activitySummary, setActivitySummary] = useState({ unread_count: 0 });
  const [activityItems, setActivityItems] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  const refreshCurrentUser = async () => {
    try {
      const response = await apiFetch(API_ENDPOINTS.AUTH_ME);
      if (!response.ok) {
        throw new Error('Unauthorized');
      }
      const data = await response.json();
      setCurrentUser(data);
      return data;
    } catch (_err) {
      setCurrentUser(null);
      return null;
    }
  };

  useEffect(() => {
    if (location.pathname.startsWith('/requests')) return;
    const fullPath = buildFullPath(location);
    lastNonRequestsPathRef.current = fullPath;
    sessionStorage.setItem(REQUESTS_RETURN_PATH_KEY, fullPath);
  }, [location]);

  useEffect(() => {
    recordWorkspaceRoute(`${location.pathname || '/'}${location.search || ''}${location.hash || ''}`);
  }, [location.hash, location.pathname, location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldOpenLogin = params.get('login') === '1';
    const error = params.get('error') || params.get('login_error') || '';
    const mfaMode = params.get('mfa');
    const needsMfa = mfaMode === 'required';
    setLoginError(error);
    setMfaRequired(needsMfa);
    if (shouldOpenLogin || error || needsMfa) {
      setLoginModalOpen(true);
    }
  }, [location.search]);

  useEffect(() => {
    if (!location.pathname.startsWith('/pdf')) return;
    const params = new URLSearchParams(location.search || '');
    const panel = String(params.get('panel') || '').trim().toLowerCase();
    const hasDirectPdfTarget = Boolean(String(params.get('pdf_id') || '').trim());
    if (panel === 'requests' && !hasDirectPdfTarget) {
      params.delete('panel');
      const returnSearch = params.toString();
      const returnTo = `${location.pathname}${returnSearch ? `?${returnSearch}` : ''}${location.hash || ''}`;
      lastNonRequestsPathRef.current = returnTo;
      sessionStorage.setItem(REQUESTS_RETURN_PATH_KEY, returnTo);
      navigate('/requests', { replace: true, state: { returnTo } });
    }
  }, [location.hash, location.pathname, location.search, navigate]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        await refreshCurrentUser();
      } finally {
        if (!cancelled) {
          setLoadingUser(false);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const isAdmin = currentUser?.role === 'admin';
  const isAuthenticated = Boolean(currentUser);
  const applicationRoute = parseApplicationRoute(location.pathname);
  const applicationCatalog = useApplicationCatalog({
    enabled: isAuthenticated,
    activeApplicationId: applicationRoute.applicationId,
  });
  const isApplicationPath = applicationRoute.isApplicationRoute;
  const applicationHomeFromCurrentRoute = applicationRoute.applicationId
    ? buildApplicationPath(applicationRoute.applicationId)
    : '';
  const resolveApplicationHomePath = useCallback(() => {
    if (applicationHomeFromCurrentRoute) return applicationHomeFromCurrentRoute;
    const storedHomePath = sanitizeApplicationHomePath(
      sessionStorage.getItem(LAST_APPLICATION_HOME_PATH_KEY)
    );
    return storedHomePath || buildApplicationsPath();
  }, [applicationHomeFromCurrentRoute]);
  const isProtectedMode = currentMode === 'pdf'
    || currentMode === 'requirements'
    || currentMode === 'api-testing'
    || currentMode === 'excel-testcases'
    || currentMode === 'feature-capture'
    || currentMode === 'applications'
    || currentMode === 'application'
    || currentMode === 'dashboard'
    || currentMode === 'admin'
    || currentMode === 'requests';

  const refreshNotifications = useCallback(async () => {
    if (!isAuthenticated) {
      setNotificationSummary({
        brd_pending: 0,
        sharing_pending: 0,
        test_case_pending: 0,
        total_pending: 0,
      });
      setNotificationItems([]);
      setActivitySummary({ unread_count: 0 });
      setActivityItems([]);
      return;
    }

    setNotificationsLoading(true);
    try {
      const [
        summaryResponse,
        pendingResponse,
        activitySummaryResponse,
        activityResponse,
      ] = await Promise.all([
        apiFetch(API_ENDPOINTS.REQUESTS_SUMMARY),
        apiFetch(`${API_ENDPOINTS.REQUESTS_PENDING}?limit=8`),
        apiFetch(API_ENDPOINTS.NOTIFICATIONS_SUMMARY),
        apiFetch(`${API_ENDPOINTS.NOTIFICATIONS}?limit=12`),
      ]);

      if (summaryResponse.ok) {
        const summaryPayload = await summaryResponse.json().catch(() => ({}));
        const summary = summaryPayload?.summary || {};
        setNotificationSummary({
          brd_pending: Number(summary?.brd_pending || 0),
          sharing_pending: Number(summary?.sharing_pending || 0),
          test_case_pending: Number(summary?.test_case_pending || 0),
          total_pending: Number(summary?.total_pending || 0),
        });
      }

      if (pendingResponse.ok) {
        const pendingPayload = await pendingResponse.json().catch(() => ({}));
        setNotificationItems(Array.isArray(pendingPayload?.items) ? pendingPayload.items : []);
      }
      if (activitySummaryResponse.ok) {
        const activityPayload = await activitySummaryResponse.json().catch(() => ({}));
        const nextSummary = activityPayload?.summary || {};
        setActivitySummary({
          unread_count: Number(nextSummary?.unread_count || 0),
        });
      }
      if (activityResponse.ok) {
        const activityPayload = await activityResponse.json().catch(() => ({}));
        setActivityItems(Array.isArray(activityPayload?.items) ? activityPayload.items : []);
        const nextSummary = activityPayload?.summary || null;
        if (nextSummary) {
          setActivitySummary({
            unread_count: Number(nextSummary?.unread_count || 0),
          });
        }
      }
    } catch (_err) {
      // Notifications are best-effort and should not block the app flow.
    } finally {
      setNotificationsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !applicationHomeFromCurrentRoute) return;
    sessionStorage.setItem(LAST_APPLICATION_HOME_PATH_KEY, applicationHomeFromCurrentRoute);
  }, [applicationHomeFromCurrentRoute, isAuthenticated]);

  useEffect(() => {
    if (!loadingUser && isProtectedMode && !isAuthenticated) {
      rememberAuthReturnPath();
      setLoginModalOpen(true);
      return;
    }
    if (!loadingUser && currentMode === 'admin' && !isAdmin) {
      navigate('/', { replace: true });
    }
  }, [currentMode, isAdmin, isAuthenticated, isProtectedMode, loadingUser, navigate, rememberAuthReturnPath]);

  useEffect(() => {
    if (loadingUser || !isAuthenticated) return;
    const returnPath = sanitizeAuthReturnPath(sessionStorage.getItem(AUTH_RETURN_PATH_KEY));
    if (!returnPath) {
      sessionStorage.removeItem(AUTH_RETURN_PATH_KEY);
      return;
    }

    const currentPath = buildFullPath(location);
    const params = new URLSearchParams(location.search || '');
    const onLoginLanding = location.pathname === '/' || params.has('login') || params.has('mfa');
    sessionStorage.removeItem(AUTH_RETURN_PATH_KEY);
    if (onLoginLanding && currentPath !== returnPath) {
      navigate(returnPath, { replace: true });
    }
  }, [isAuthenticated, loadingUser, location, navigate]);

  useEffect(() => {
    if (loadingUser || !isAuthenticated) return;
    const legacyModuleRoots = {
      '/pdf': 'brd',
      '/requirements': 'requirements',
      '/api-testing': 'api',
      '/excel-testcases': 'excel',
      '/feature-capture': 'feature',
    };
    const legacyModule = legacyModuleRoots[location.pathname];
    if (legacyModule) {
      navigate(buildApplicationsPath(legacyModule), { replace: true });
      return;
    }
    if (location.pathname === '/' && !location.search) {
      navigate(resolveApplicationHomePath(), { replace: true });
    }
  }, [
    isAuthenticated,
    loadingUser,
    location.pathname,
    location.search,
    navigate,
    resolveApplicationHomePath,
  ]);

  useEffect(() => {
    if (!isAuthenticated) {
      setNotificationSummary({
        brd_pending: 0,
        sharing_pending: 0,
        test_case_pending: 0,
        total_pending: 0,
      });
      setNotificationItems([]);
      setActivitySummary({ unread_count: 0 });
      setActivityItems([]);
      return undefined;
    }

    let cancelled = false;
    const run = async () => {
      if (cancelled) return;
      await refreshNotifications();
    };

    run();
    const timerId = window.setInterval(run, 45000);
    return () => {
      cancelled = true;
      window.clearInterval(timerId);
    };
  }, [isAuthenticated, currentUser?.id, refreshNotifications]);

  const transitionToMode = (mode) => {
    const protectedModes = new Set([
      'applications',
      'application',
      'pdf',
      'requirements',
      'api-testing',
      'excel-testcases',
      'feature-capture',
      'dashboard',
      'admin',
      'requests',
    ]);
    if (protectedModes.has(mode) && !isAuthenticated) {
      rememberAuthReturnPath(routeForMode(mode));
      setLoginModalOpen(true);
      return;
    }
    if (mode === 'admin' && !isAdmin) {
      navigate('/');
      return;
    }
    const nextPath = mode === 'landing' && isAuthenticated
      ? buildApplicationsPath()
      : routeForMode(mode);
    if (nextPath !== buildFullPath(location)) {
      navigate(nextPath);
    }
  };

  const handleLogout = async () => {
    try {
      await apiFetch(API_ENDPOINTS.AUTH_LOGOUT, {
        method: 'POST',
      });
    } catch (_err) {
      // Logout navigation should still happen even if API call fails.
    }

    sessionStorage.removeItem('active_pdf_id');
    sessionStorage.removeItem(LAST_APPLICATION_HOME_PATH_KEY);

    setCurrentUser(null);
    setNotificationSummary({
      brd_pending: 0,
      sharing_pending: 0,
      test_case_pending: 0,
      total_pending: 0,
    });
    setNotificationItems([]);
    setActivitySummary({ unread_count: 0 });
    setActivityItems([]);
    setMfaModalOpen(false);
    navigate('/', { replace: true });
  };

  const handleOpenLogin = () => {
    setLoginError('');
    setMfaRequired(false);
    if (isProtectedMode) {
      rememberAuthReturnPath();
    } else if (currentMode === 'landing') {
      rememberAuthReturnPath(resolveApplicationHomePath());
    }
    setLoginModalOpen(true);
  };

  const handleCloseLogin = () => {
    setLoginModalOpen(false);
    setMfaRequired(false);
    if (!isAuthenticated) {
      sessionStorage.removeItem(AUTH_RETURN_PATH_KEY);
    }
    if (!isAuthenticated && isProtectedMode) {
      navigate('/', { replace: true });
      return;
    }
    if (location.pathname === '/' && location.search) {
      navigate('/', { replace: true });
    }
  };

  const handleMfaVerified = async () => {
    setLoadingUser(true);
    await refreshCurrentUser();
    setLoadingUser(false);
    setLoginError('');
    setMfaRequired(false);
    setLoginModalOpen(false);
    const returnPath = consumeAuthReturnPath();
    if (returnPath) {
      navigate(returnPath, { replace: true });
    } else if (location.search) {
      navigate(location.pathname || '/', { replace: true });
    }
  };

  const handleOpenSecurity = () => {
    setMfaModalOpen(true);
  };

  const handleCloseSecurity = () => {
    setMfaModalOpen(false);
  };

  const handleOpenRequests = () => {
    if (!isAuthenticated) {
      setLoginModalOpen(true);
      return;
    }
    const returnTo = sanitizeReturnPath(
      location.pathname.startsWith('/requests')
        ? lastNonRequestsPathRef.current
        : buildFullPath(location),
      lastNonRequestsPathRef.current || buildApplicationsPath()
    );
    lastNonRequestsPathRef.current = returnTo;
    sessionStorage.setItem(REQUESTS_RETURN_PATH_KEY, returnTo);
    navigate('/requests', { state: { returnTo } });
  };

  const handleCloseRequests = () => {
    const previousRoute = consumeWorkspaceBackRoute(buildFullPath(location), {
      skipRoutes: ['/requests'],
      ignoreStoredStack: wasCurrentDocumentReloaded(),
    });
    if (previousRoute) {
      navigate(previousRoute);
      return;
    }

    const locationState = location.state && typeof location.state === 'object' ? location.state : {};
    const storedReturnTo = sessionStorage.getItem(REQUESTS_RETURN_PATH_KEY);
    const returnTo = sanitizeReturnPath(
      locationState.returnTo || storedReturnTo || lastNonRequestsPathRef.current,
      buildApplicationsPath()
    );
    navigate(returnTo);
  };

  const openRequestTarget = useCallback((item) => {
    const nextPath = resolveRequestPath(item);
    navigate(nextPath || '/requests');
  }, [navigate]);

  const handleOpenRequestItem = useCallback((item) => {
    openRequestTarget(item);
  }, [openRequestTarget]);

  const handleOpenActivityItem = useCallback(async (item) => {
    if (!isAuthenticated) return;

    const notificationId = String(item?.id || '').trim();
    const isUnread = String(item?.status || '').toLowerCase() !== 'read';

    if (notificationId && isUnread) {
      try {
        await apiFetch(API_ENDPOINTS.NOTIFICATION_READ(notificationId), {
          method: 'POST',
        });
      } catch (_err) {
        // Navigation still helps even if the unread state refresh lags.
      }
    }

    openRequestTarget(item);
    refreshNotifications();
  }, [isAuthenticated, openRequestTarget, refreshNotifications]);

  const handleMarkNotificationsRead = async () => {
    if (!isAuthenticated) return;
    try {
      await apiFetch(API_ENDPOINTS.NOTIFICATIONS_READ_ALL, {
        method: 'POST',
      });
      await refreshNotifications();
    } catch (_err) {
      // Best-effort; leaving unread state intact is safer than hiding it.
    }
  };

  const handleSwitchApplication = (applicationId) => {
    const normalizedId = String(applicationId || '').trim();
    navigate(normalizedId ? buildApplicationPath(normalizedId) : buildApplicationsPath());
  };

  const handleHome = () => {
    const nextPath = isAuthenticated ? resolveApplicationHomePath() : routeForMode('landing');
    if (nextPath !== buildFullPath(location)) {
      navigate(nextPath);
    }
  };

  if (loadingUser && isProtectedMode) {
    return (
      <div className="app-shell">
        <AuthHydrationScreen />
      </div>
    );
  }

  // The dashboard brings its own sidebar and top bar, so it renders full-bleed:
  // no app Header, and outside .route-content-frame — that wrapper keeps a
  // transform after its enter animation, which would trap the dashboard's
  // fixed-position drill-down panels inside it instead of the viewport.
  // Unauthenticated visits fall through to the normal shell, which opens login.
  if (currentMode === 'dashboard' && isAuthenticated) {
    return (
      <DashboardWorkspace
        currentUser={currentUser}
        onExit={handleHome}
        onLogout={handleLogout}
      />
    );
  }

  const pageContent = (
    <>
      {currentMode === 'landing' && (
        <ProductLanding
          onSelectPDF={() => transitionToMode('pdf')}
          onSelectRequirements={() => transitionToMode('requirements')}
          onSelectApiTesting={() => transitionToMode('api-testing')}
          onSelectExcelTestCases={() => transitionToMode('excel-testcases')}
          onSelectFeatureCapture={() => transitionToMode('feature-capture')}
          onOpenApplications={() => transitionToMode('applications')}
        />
      )}

      {!loadingUser && isApplicationPath && isAuthenticated && (
        <ApplicationPortal
          currentUser={currentUser}
          catalog={applicationCatalog}
        />
      )}

      {!loadingUser && !isApplicationPath && currentMode === 'pdf' && isAuthenticated && (
        <PDFWorkflow currentUser={currentUser} intakeMode="document" />
      )}
      {!loadingUser && !isApplicationPath && currentMode === 'requirements' && isAuthenticated && (
        <PDFWorkflow currentUser={currentUser} intakeMode="text" />
      )}
      {!loadingUser && !isApplicationPath && currentMode === 'api-testing' && isAuthenticated && (
        <PDFWorkflow currentUser={currentUser} intakeMode="api" />
      )}
      {!loadingUser && !isApplicationPath && currentMode === 'excel-testcases' && isAuthenticated && (
        <PDFWorkflow currentUser={currentUser} intakeMode="excel" />
      )}
      {!loadingUser && !isApplicationPath && currentMode === 'feature-capture' && isAuthenticated && (
        <FeatureCaptureWorkspace currentUser={currentUser} />
      )}
      {!loadingUser && currentMode === 'admin' && isAuthenticated && isAdmin && (
        <AdminInvitesPage currentUser={currentUser} />
      )}
      {!loadingUser && currentMode === 'requests' && isAuthenticated && (
        <RequestsCenterPage
          currentUser={currentUser}
          onBack={handleCloseRequests}
          onRequestsChanged={refreshNotifications}
        />
      )}
    </>
  );

  return (
    <JobProvider enabled={isAuthenticated}>
      <div className="app-shell">
      <Header
        currentMode={currentMode}
        onHome={handleHome}
        onAdmin={() => transitionToMode('admin')}
        onOpenDashboard={() => transitionToMode('dashboard')}
        onSecurity={handleOpenSecurity}
        onLogin={handleOpenLogin}
        onLogout={handleLogout}
        onOpenRequests={handleOpenRequests}
        onOpenRequestItem={handleOpenRequestItem}
        onOpenActivityItem={handleOpenActivityItem}
        onSwitchWorkspace={transitionToMode}
        applications={applicationCatalog.applications}
        activeApplication={applicationCatalog.activeApplication}
        applicationsLoading={applicationCatalog.loading}
        isApplicationContext={isApplicationPath}
        onSwitchApplication={handleSwitchApplication}
        onOpenApplications={() => navigate(buildApplicationsPath())}
        isAdmin={isAdmin}
        isAuthenticated={isAuthenticated}
        currentUser={currentUser}
        notificationSummary={notificationSummary}
        notificationItems={notificationItems}
        activitySummary={activitySummary}
        activityItems={activityItems}
        notificationsLoading={notificationsLoading}
        onRefreshNotifications={refreshNotifications}
        onMarkNotificationsRead={handleMarkNotificationsRead}
      />

      <div key={currentMode} className="route-content-frame">
        {pageContent}
      </div>

      <LoginModal
        isOpen={loginModalOpen}
        error={loginError}
        mfaRequired={mfaRequired}
        onMfaVerified={handleMfaVerified}
        onClose={handleCloseLogin}
      />
      <MFAManagementModal
        isOpen={mfaModalOpen}
        onClose={handleCloseSecurity}
        onMfaChanged={refreshCurrentUser}
      />
      <JobToastRegion />
      </div>
    </JobProvider>
  );
};

function App() {
  return (
    <BrowserRouter unstable_useTransitions={false}>
      <Routes>
        <Route path="/login" element={<Navigate to="/?login=1" replace />} />
        <Route path="/app" element={<Navigate to="/" replace />} />
        <Route path="/app/pdf" element={<Navigate to="/pdf" replace />} />
        <Route path="/app/requirements" element={<Navigate to="/requirements" replace />} />
        <Route path="/app/api-testing" element={<Navigate to="/api-testing" replace />} />
        <Route path="/app/excel-testcases" element={<Navigate to="/excel-testcases" replace />} />
        <Route path="/app/feature-capture" element={<Navigate to="/feature-capture" replace />} />
        <Route path="/app/admin" element={<Navigate to="/admin" replace />} />
        <Route path="/app/requests" element={<Navigate to="/requests" replace />} />
        <Route path="/*" element={<WorkspaceShell />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
